/**
 * useNormalization - Hook para normalización automática de coordenadas
 * 
 * Procesa infraestructuras extraídas y normaliza sus coordenadas UTM30.
 * Se ejecuta automáticamente después de la extracción del documento.
 * 
 * F6.02: Integración Web Worker con fallback automático.
 * - Si Worker disponible → hilo separado (UI no se bloquea)
 * - Si Worker no disponible o falla → procesamiento por lotes en hilo principal
 * 
 * @module hooks/useNormalization
 * @version 2.0.0 - F6.02 Worker Integration
 */

import { useState, useCallback, useRef } from 'react'
import { 
  procesarParCoordenadas, 
  toNormalizationPairResult,
  type ParCoordenadas 
} from '../lib/coordinateNormalizer'
import type { ExtractedInfrastructure, ProcessedInfrastructure } from '../types/processing'
import { NormalizationWorkerService } from '../services/NormalizationWorkerService'

// ============================================================================
// TIPOS
// ============================================================================

export interface NormalizationProgress {
  /** Infraestructuras procesadas */
  processed: number
  /** Total a procesar */
  total: number
  /** Porcentaje completado */
  percentage: number
  /** B-07: Nombre de la infra que se está procesando en este momento */
  currentInfraName?: string
}

export interface NormalizationStats {
  /** Total de infraestructuras procesadas */
  total: number
  /** Normalizaciones exitosas (score >= 50) */
  success: number
  /** Normalizaciones con warnings (score 50-69) */
  warnings: number
  /** Normalizaciones críticas (score < 50) */
  critical: number
  /** Sin coordenadas (no normalizadas) */
  noCoords: number
  /** Score promedio */
  averageScore: number
}

export interface UseNormalizationResult {
  /** Ejecutar normalización en lote */
  normalizeAll: (infrastructures: ExtractedInfrastructure[], ineCode?: string) => Promise<ProcessedInfrastructure[]>
  /** Estado de procesamiento */
  isNormalizing: boolean
  /** Progreso actual */
  progress: NormalizationProgress
  /** Resultados procesados */
  results: ProcessedInfrastructure[]
  /** Estadísticas de normalización */
  stats: NormalizationStats | null
  /** Error si ocurrió */
  error: string | null
  /** Resetear estado */
  reset: () => void
  /** BUG-7: actualizar campos de una infraestructura por id (edición manual) */
  patchResult: (id: string, patch: Partial<ProcessedInfrastructure>) => void
}

// ============================================================================
// CONSTANTES
// ============================================================================

/** Tamaño del lote para procesamiento chunked (evita bloquear UI) */
const BATCH_SIZE = 20

/** Delay entre lotes para permitir render (ms) */
const BATCH_DELAY = 10

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Procesa una infraestructura y devuelve su versión normalizada.
 * Función pura sin dependencias DOM — apta para Web Worker.
 */
export function normalizeInfrastructure(
  infra: ExtractedInfrastructure,
  ineCode?: string
): ProcessedInfrastructure {
  // Si no tiene coordenadas, devolver como pending sin normalización
  if (!infra.hasCoordinates || !infra.xOriginal || !infra.yOriginal) {
    return {
      ...infra,
      score: 0,
      source: 'pending',
      status: 'pending',
      confidence: 'NULA'
    }
  }

  // Procesar par de coordenadas
  const parResult: ParCoordenadas = procesarParCoordenadas(
    infra.xOriginal,
    infra.yOriginal,
    { codigoINE: ineCode }
  )

  // Convertir a formato NormalizationPairResult
  const normResult = toNormalizationPairResult(parResult)

  // Determinar si es válido (ambas coordenadas normalizadas con éxito)
  const esValido = parResult.normalizacionX.exito && parResult.normalizacionY.exito && 
                   parResult.x !== null && parResult.y !== null

  // Calcular score (0-100) basado en confianza
  const confidenceToScore: Record<string, number> = {
    'HIGH': 95,
    'MEDIUM': 75,
    'LOW': 55,
    'CRITICAL': 25
  }
  const score = confidenceToScore[normResult.confidence] ?? 50

  // Mapear confianza a tipo esperado por ProcessedInfrastructure
  const confidenceMap: Record<string, 'CRITICA' | 'ALTA' | 'MEDIA' | 'BAJA' | 'NULA'> = {
    'HIGH': 'ALTA',
    'MEDIUM': 'MEDIA',
    'LOW': 'BAJA',
    'CRITICAL': 'CRITICA'
  }
  const confidence = confidenceMap[normResult.confidence] ?? 'NULA'

  // Determinar nuevo status
  const newStatus = esValido ? 'original' : 'pending'

  return {
    ...infra,
    xFinal: parResult.x ?? undefined,
    yFinal: parResult.y ?? undefined,
    normalization: {
      original: infra.xOriginal + ', ' + infra.yOriginal,
      normalized: parResult.x,
      pattern: parResult.normalizacionX.patronDetectado,
      isValid: esValido,
      confidence: normResult.confidence,
      score,
      corrections: normResult.corrections,
      warnings: normResult.warnings,
      errors: normResult.errors,
      // Campos extendidos de NormalizationPairResult
      x: parResult.x,
      y: parResult.y,
      xOriginal: parResult.xOriginal,
      yOriginal: parResult.yOriginal
    },
    score,
    confidence,
    source: esValido ? 'normalized' : 'pending',
    status: newStatus
  }
}

/**
 * Calcula estadísticas de normalización.
 * Función pura — apta para Web Worker.
 */
export function calculateStats(results: ProcessedInfrastructure[]): NormalizationStats {
  const withCoords = results.filter(r => r.normalization)
  const noCoords = results.filter(r => !r.normalization)
  
  const success = withCoords.filter(r => r.score >= 70)
  const warnings = withCoords.filter(r => r.score >= 50 && r.score < 70)
  const critical = withCoords.filter(r => r.score < 50)
  
  const totalScore = withCoords.reduce((sum, r) => sum + r.score, 0)
  const averageScore = withCoords.length > 0 
    ? Math.round(totalScore / withCoords.length) 
    : 0

  return {
    total: results.length,
    success: success.length,
    warnings: warnings.length,
    critical: critical.length,
    noCoords: noCoords.length,
    averageScore
  }
}

/**
 * Delay helper para permitir render entre lotes
 */
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

// ============================================================================
// HOOK
// ============================================================================

/** Singleton del Worker service — compartido entre instancias del hook */
let workerService: NormalizationWorkerService | null = null

function getWorkerService(): NormalizationWorkerService | null {
  if (!NormalizationWorkerService.isAvailable()) return null
  if (!workerService) {
    workerService = new NormalizationWorkerService()
  }
  return workerService
}

export function useNormalization(): UseNormalizationResult {
  const [isNormalizing, setIsNormalizing] = useState(false)
  const [progress, setProgress] = useState<NormalizationProgress>({ 
    processed: 0, 
    total: 0, 
    percentage: 0 
  })
  const [results, setResults] = useState<ProcessedInfrastructure[]>([])
  const [stats, setStats] = useState<NormalizationStats | null>(null)
  const [error, setError] = useState<string | null>(null)
  
  // Ref para evitar múltiples ejecuciones simultáneas
  const processingRef = useRef(false)

  // CAMDOC: generación de la tanda (ver useAutoGeocoding). `reset()` la
  // incrementa y una normalización tardía del documento anterior no escribe.
  const generationRef = useRef(0)

  /**
   * Fallback: procesamiento por lotes en hilo principal con yield entre lotes.
   */
  const normalizeMainThread = useCallback(async (
    infrastructures: ExtractedInfrastructure[],
    ineCode?: string
  ): Promise<ProcessedInfrastructure[]> => {
    const processed: ProcessedInfrastructure[] = []
    
    for (let i = 0; i < infrastructures.length; i += BATCH_SIZE) {
      const batch = infrastructures.slice(i, i + BATCH_SIZE)
      
      for (const infra of batch) {
        processed.push(normalizeInfrastructure(infra, ineCode))
      }
      
      const current = Math.min(i + BATCH_SIZE, infrastructures.length)
      // B-07: incluir nombre de la última infra del lote procesado
      const lastInfra = batch[batch.length - 1]
      setProgress({
        processed: current,
        total: infrastructures.length,
        percentage: Math.round((current / infrastructures.length) * 100),
        currentInfraName: lastInfra?.nombre ?? undefined,
      })
      
      if (i + BATCH_SIZE < infrastructures.length) {
        await delay(BATCH_DELAY)
      }
    }

    return processed
  }, [])

  const normalizeAll = useCallback(async (
    infrastructures: ExtractedInfrastructure[],
    ineCode?: string
  ): Promise<ProcessedInfrastructure[]> => {
    if (processingRef.current) {
      console.warn('[useNormalization] Ya hay una normalización en curso')
      return results
    }

    const generation = generationRef.current
    const vigente = () => generation === generationRef.current

    processingRef.current = true
    setIsNormalizing(true)
    setError(null)
    setProgress({ processed: 0, total: infrastructures.length, percentage: 0 })

    try {
      let processed: ProcessedInfrastructure[]

      // F6.02: Intentar Worker → fallback hilo principal
      const service = getWorkerService()
      if (service) {
        try {
          const workerResult = await service.normalize(
            infrastructures,
            ineCode,
            {
              onProgress: (p, t) => setProgress({
                processed: p,
                total: t,
                percentage: Math.round((p / t) * 100)
              }),
              batchSize: BATCH_SIZE,
            }
          )
          processed = workerResult.items
          console.log('[useNormalization] Procesado via Web Worker')
        } catch (workerErr) {
          console.warn('[useNormalization] Worker falló, fallback a hilo principal:', workerErr)
          processed = await normalizeMainThread(infrastructures, ineCode)
        }
      } else {
        processed = await normalizeMainThread(infrastructures, ineCode)
      }

      if (!vigente()) {
        console.info('[useNormalization] CAMDOC — normalización descartada: el documento se canceló o cambió')
        return []
      }

      const finalStats = calculateStats(processed)
      setResults(processed)
      setStats(finalStats)
      
      console.log(`[useNormalization] Normalización completada:`, finalStats)
      return processed
      
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido'
      if (vigente()) setError(message)
      console.error('[useNormalization] Error:', err)
      return []
      
    } finally {
      if (vigente()) {
        setIsNormalizing(false)
        processingRef.current = false
      }
    }
  }, [results, normalizeMainThread])

  const reset = useCallback(() => {
    generationRef.current += 1
    setIsNormalizing(false)
    setProgress({ processed: 0, total: 0, percentage: 0 })
    setResults([])
    setStats(null)
    setError(null)
    processingRef.current = false
  }, [])

  // BUG-7: actualiza campos de un resultado por id sin mutar los demás
  const patchResult = useCallback((id: string, patch: Partial<ProcessedInfrastructure>) => {
    setResults(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r))
  }, [])

  return {
    normalizeAll,
    isNormalizing,
    progress,
    results,
    stats,
    error,
    reset,
    patchResult,
  }
}

export default useNormalization
